import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  RequestQuote as RequestQuoteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import DataTable from "../shared/DataTable";
import CustomerAutocomplete from "../shared/CustomerAutocomplete";
import { codeFont } from "../../utils/fonts";

const EMPTY_FORM = { cid: "", password: "" };

function InsurerMapping({ organizationId }) {
  const { t } = useTranslation();
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const resp = await axios.get(`/api/cabas/insurers/?organization=${organizationId}`);
      setInsurers(resp.data);
    } catch (err) {
      setLoadError(err.response?.data?.error || t("cabas.settings.insurersLoadError"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (insurer) => {
    setEditing(insurer);
    setCustomer(insurer.customer && !insurer.customer.is_hidden ? insurer.customer : null);
    setError("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(`/api/cabas/insurers/${editing.id}/?organization=${organizationId}`, {
        customer: customer ? customer.id : null,
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.customer?.[0] || err.response?.data?.error || t("cabas.settings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="cabas-insurers">
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {t("cabas.settings.insurersTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("cabas.settings.insurersHelp")}
      </Typography>
      {loadError && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="cabas-insurers-error">
          {loadError}
        </Alert>
      )}
      <DataTable
        columns={[
          {
            key: "code",
            label: t("cabas.settings.insurerCode"),
            width: "1%",
            noWrap: true,
            render: (row) => <Box component="span" sx={codeFont}>{row.insurance_company_code}</Box>,
          },
          { key: "name", label: t("cabas.settings.insurerName"), maxWidth: 260, render: (row) => row.name },
          {
            key: "customer",
            label: t("cabas.settings.insurerCustomer"),
            maxWidth: 260,
            render: (row) =>
              row.customer && !row.customer.is_hidden ? (
                row.customer.name
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("cabas.settings.insurerUnmapped")}
                </Typography>
              ),
          },
          {
            key: "actions",
            label: "",
            width: "1%",
            align: "right",
            render: (row) => (
              <Button size="small" onClick={() => openEdit(row)} data-testid={`cabas-insurer-edit-${row.id}`}>
                {t("Edit")}
              </Button>
            ),
          },
        ]}
        data={insurers}
        loading={loading}
        emptyTitle={t("cabas.settings.insurersEmpty")}
      />

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editing?.name || editing?.insurance_company_code}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("cabas.settings.insurerDialogHelp")}</DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <CustomerAutocomplete
            value={customer}
            onChange={setCustomer}
            label={t("cabas.settings.insurerCustomer")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button variant="contained" onClick={save} disabled={saving} data-testid="cabas-insurer-save">
            {saving ? <CircularProgress size={16} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function CabasIntegrationCard() {
  const { t } = useTranslation();
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  // Set when the status could not be loaded: the card then shows the error and a retry,
  // never "Ota käyttöön" as if nothing were configured.
  const [loadError, setLoadError] = useState("");

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setLoadError("");
    try {
      const resp = await axios.get(`/api/cabas/integration/?organization=${organizationId}`);
      setData(resp.data);
    } catch (err) {
      setData(null);
      setLoadError(err.response?.data?.error || t("cabas.settings.loadError"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const startEdit = () => {
    setForm({ cid: data?.cid || "", password: "" });
    setError("");
    setTestResult(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setBusy(true);
    setError("");
    setTestResult(null);
    try {
      const url = `/api/cabas/integration/?organization=${organizationId}`;
      // The CID and password are one credential pair: both are always sent.
      const resp = data?.configured ? await axios.patch(url, form) : await axios.post(url, form);
      setData(resp.data);
      setEditing(false);
      setForm(EMPTY_FORM);
      await fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || t("cabas.settings.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await axios.post(`/api/cabas/integration/test/?organization=${organizationId}`);
      setTestResult({ success: true });
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || t("cabas.settings.testError") });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setConfirmRemoveOpen(false);
    try {
      await axios.delete(`/api/cabas/integration/?organization=${organizationId}`);
      setEditing(false);
      setForm(EMPTY_FORM);
      setTestResult(null);
      await fetchStatus();
      await fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || t("cabas.settings.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const canSave = !busy && form.cid.trim() && form.password;

  return (
    <Card variant="outlined" sx={{ gridColumn: { md: "1 / -1" } }} data-testid="cabas-card">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2, gap: 1, flexWrap: "wrap" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <RequestQuoteIcon />
          </Box>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {data?.environment === "qa" && (
              <Chip size="small" variant="outlined" color="warning" label={t("cabas.settings.qaEnvironment")} />
            )}
            {data?.configured && <Chip size="small" color="success" label={t("Active")} />}
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          {t("cabas.settings.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("cabas.settings.description")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : loadError ? (
          <Alert
            severity="error"
            data-testid="cabas-load-error"
            action={
              <Button color="inherit" size="small" onClick={fetchStatus} data-testid="cabas-retry">
                {t("cabas.settings.retry")}
              </Button>
            }
          >
            {loadError}
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="cabas-error">
                {error}
              </Alert>
            )}
            {testResult && (
              <Alert severity={testResult.success ? "success" : "error"} sx={{ mb: 2 }} data-testid="cabas-test-result">
                {testResult.success ? t("cabas.settings.testSuccess") : testResult.error}
              </Alert>
            )}

            {!editing && data?.configured && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("cabas.settings.connectedAs")}: <code>{data.cid}</code>
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" size="small" onClick={handleTest} disabled={busy} data-testid="cabas-test">
                    {busy ? <CircularProgress size={16} /> : t("cabas.settings.testConnection")}
                  </Button>
                  <Button variant="text" size="small" onClick={startEdit} disabled={busy} data-testid="cabas-edit">
                    {t("Edit")}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => setConfirmRemoveOpen(true)}
                    disabled={busy}
                    data-testid="cabas-remove"
                  >
                    {t("cabas.settings.remove")}
                  </Button>
                </Box>
                <InsurerMapping organizationId={organizationId} />
              </>
            )}

            {!editing && !data?.configured && (
              <Button variant="contained" size="small" onClick={startEdit} data-testid="cabas-configure">
                {t("cabas.settings.configure")}
              </Button>
            )}

            {editing && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("cabas.settings.cid")}
                  helperText={t("cabas.settings.cidHelp")}
                  value={form.cid}
                  onChange={(e) => setForm({ ...form, cid: e.target.value })}
                  inputProps={{ "data-testid": "cabas-cid" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type={showPassword ? "text" : "password"}
                  label={t("cabas.settings.password")}
                  helperText={data?.configured ? t("cabas.settings.passwordRequiredHelp") : ""}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  inputProps={{ "data-testid": "cabas-password" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!canSave}
                    data-testid="cabas-save"
                  >
                    {busy ? <CircularProgress size={16} /> : t("Save")}
                  </Button>
                  <Button variant="text" size="small" onClick={() => setEditing(false)} disabled={busy}>
                    {t("Cancel")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}

        <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
          <DialogTitle>{t("cabas.settings.removeConfirmTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("cabas.settings.removeConfirmText")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)}>{t("Cancel")}</Button>
            <Button color="error" onClick={handleRemove} data-testid="cabas-remove-confirm">
              {t("cabas.settings.remove")}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
